import { atomToPdbRow } from './pdbAssembly.js';

// Residue reader for PDB and mmCIF text, backed by molstar's structure model. molstar is loaded on
// first use, so pages that only need the text helpers at the bottom do not pull it in.

let OrderedSet, CIF, CifCategory, CifField, CifWriter, parsePDB, trajectoryFromMmCIF, trajectoryFromPDB,
    getProteinOneLetterCode, Queries, Structure, StructureElement, StructureProperties, StructureQuery,
    StructureSelection, Unit, to_mmCIF, Task;
let molstarLoading = null;

function loadMolstar() {
    if (molstarLoading) return molstarLoading;
    molstarLoading = Promise.all([
        import('molstar/lib/mol-data/int.js'),
        import('molstar/lib/mol-io/reader/cif.js'),
        import('molstar/lib/mol-io/writer/cif.js'),
        import('molstar/lib/mol-io/reader/pdb/parser.js'),
        import('molstar/lib/mol-model-formats/structure/mmcif.js'),
        import('molstar/lib/mol-model-formats/structure/pdb.js'),
        import('molstar/lib/mol-model/sequence/constants.js'),
        import('molstar/lib/mol-model/structure.js'),
        import('molstar/lib/mol-model/structure/export/mmcif.js'),
        import('molstar/lib/mol-task/index.js'),
    ]).then(([int, cifReader, cifWriter, pdb, mmcifFormat, pdbFormat, sequence, structure, mmcifExport, task]) => {
        ({ OrderedSet } = int);
        ({ CIF, CifCategory, CifField } = cifReader);
        ({ CifWriter } = cifWriter);
        ({ parsePDB } = pdb);
        ({ trajectoryFromMmCIF } = mmcifFormat);
        ({ trajectoryFromPDB } = pdbFormat);
        ({ getProteinOneLetterCode } = sequence);
        ({ Queries, Structure, StructureElement, StructureProperties, StructureQuery, StructureSelection, Unit } = structure);
        ({ to_mmCIF } = mmcifExport);
        ({ Task } = task);
    });
    return molstarLoading;
}

function isCif(text) {
    const head = text.trimStart();
    return head.startsWith('data_') || head.startsWith('#') || head.includes('_atom_site.');
}

// molstar reads PDB by fixed column and mis-reads a shifted line without raising
function repairAtomLine(line) {
    if (!line.startsWith('ATOM')) return line;
    let out = line;
    if (out.length > 60 && out[60] !== ' ') out = out.slice(0, 30) + out.slice(31);
    if (out.length > 20 && out[20] !== ' ') out = out.slice(0, 17) + out.slice(18);
    return out;
}

async function cifFrame(text) {
    await loadMolstar();
    const parsed = await CIF.parse(text).run();
    return parsed.isError ? null : parsed.result.blocks[0];
}

async function firstModelStructure(trajectory) {
    if (!trajectory || trajectory.frameCount === 0) return null;
    return Structure.ofModel(await Task.resolveInContext(trajectory.getFrameAtIndex(0)));
}

async function buildStructure(text) {
    await loadMolstar();
    if (isCif(text)) {
        const frame = await cifFrame(text);
        return frame ? firstModelStructure(await trajectoryFromMmCIF(frame).run()) : null;
    }
    const repaired = normalizePdb(text).split('\n').map(repairAtomLine).join('\n');
    const parsed = await parsePDB(repaired).run();
    return parsed.isError ? null : firstModelStructure(await trajectoryFromPDB(parsed.result).run());
}

// One motif check reads the same text several times over.
let cachedText = null;
let cachedStructure = null;

async function parseStructure(text) {
    if (typeof text !== 'string' || text.trim() === '') return null;
    if (text === cachedText) return cachedStructure;
    const structure = await buildStructure(text);
    cachedText = text;
    cachedStructure = structure;
    return structure;
}

/** Visits atoms in file order; molstar groups its units by entity, which reorders the chains. */
function eachAtom(structure, visit) {
    const location = StructureElement.Location.create(structure);
    const atoms = [];
    for (const unit of structure.units) {
        if (!Unit.isAtomic(unit)) continue;
        location.unit = unit;
        for (let i = 0, il = OrderedSet.size(unit.elements); i < il; i++) {
            location.element = OrderedSet.getAt(unit.elements, i);
            atoms.push([StructureProperties.atom.sourceIndex(location), unit, location.element]);
        }
    }

    atoms.sort((a, b) => a[0] - b[0]);
    for (const [, unit, element] of atoms) {
        location.unit = unit;
        location.element = element;
        visit(location);
    }
}

function chainOf(location) {
    return StructureProperties.chain.auth_asym_id(location)
        || StructureProperties.chain.label_asym_id(location);
}

function insCodeOf(location) {
    return StructureProperties.residue.pdbx_PDB_ins_code(location) || '';
}

/** Insertion codes distinguish residues that share a number (antibody numbering does this). */
function residueKey(location) {
    return `${chainOf(location)}|${StructureProperties.residue.auth_seq_id(location)}`
        + `|${insCodeOf(location)}`;
}

/**
 * Every residue in a PDB or mmCIF string, in file order, deduplicated by (chain, residue number,
 * insertion code).
 *
 * @param {string} text
 * @returns {Promise<{chain: string, resno: string, resName: string, insCode?: string, hetero: boolean}[]>}
 */
export async function listResidues(text) {
    const structure = await parseStructure(text);
    if (!structure) return [];

    const residues = [];
    const seen = new Set();
    eachAtom(structure, (location) => {
        const key = residueKey(location);
        if (seen.has(key)) return;
        seen.add(key);
        const insCode = insCodeOf(location);
        residues.push({
            chain: chainOf(location),
            resno: String(StructureProperties.residue.auth_seq_id(location)),
            resName: StructureProperties.atom.label_comp_id(location),
            ...(insCode ? { insCode } : {}),
            hetero: StructureProperties.residue.group_PDB(location) === 'HETATM',
        });
    });
    return residues;
}

export async function residueTokenSet(text) {
    const tokens = new Set();
    for (const r of await listResidues(text)) {
        tokens.add(`${r.chain}${r.resno}`);
        tokens.add(String(r.resno));
    }
    return tokens;
}

// Chain-grouped CA traces.

/** An alternate location repeats an atom per conformer; either would double the trace. */
const KEPT_ALT_LOC = new Set(['', 'A']);

/** molstar's table has no PHL, and pdbAssembly's OneToThree maps O back to it. */
const LOCAL_ONE_LETTER = { PHL: 'O', XAA: 'X' };

function oneLetter(resName) {
    const compId = resName?.toUpperCase() ?? '';
    return LOCAL_ONE_LETTER[compId] ?? getProteinOneLetterCode(compId);
}

/** Every readable C-alpha coordinate, in file order and the query structure's residue numbering. */
export async function listCaResidues(text) {
    const structure = await parseStructure(text);
    if (!structure) return [];

    const rows = [];
    const seen = new Set();
    eachAtom(structure, (location) => {
        if (StructureProperties.atom.label_atom_id(location) !== 'CA') return;
        // HETATM is not part of the chain trace.
        if (StructureProperties.residue.group_PDB(location) !== 'ATOM') return;
        if (!KEPT_ALT_LOC.has(StructureProperties.atom.label_alt_id(location))) return;

        const key = residueKey(location);
        if (seen.has(key)) return;
        seen.add(key);
        const insCode = insCodeOf(location);
        rows.push({
            chain: chainOf(location),
            resName: StructureProperties.atom.label_comp_id(location),
            resno: String(StructureProperties.residue.auth_seq_id(location)),
            xyz: [
                StructureProperties.atom.x(location),
                StructureProperties.atom.y(location),
                StructureProperties.atom.z(location),
            ],
            ...(insCode ? { insCode } : {}),
        });
    });
    return rows;
}

/**
 * The CA trace of each chain, in file order.
 *
 * @param {string} text  PDB or mmCIF
 * @returns {Promise<{chain: string, residueCount: number, ca: string, seq: string}[]>}
 *   `ca` is comma-separated x,y,z triplets and `seq` one-letter codes — exactly the pair a search hit
 *   arrives as, so mockPDB(ca, seq, chain) works on either without a second code path.
 */
export async function listChains(text) {
    const rows = await listCaResidues(text);

    const byChain = new Map();
    for (const row of rows) {
        const chain = row.chain || 'A';
        if (!byChain.has(chain)) byChain.set(chain, { chain, xyz: [], seq: [] });
        const entry = byChain.get(chain);
        entry.xyz.push(...row.xyz);
        entry.seq.push(oneLetter(row.resName));
    }

    return [...byChain.values()].map(e => ({
        chain: e.chain,
        residueCount: e.seq.length,
        ca: e.xyz.map(v => v.toFixed(3)).join(','),
        seq: e.seq.join(''),
    }));
}

/**
 * An mmCIF holding only the named chains' polymer residues.
 *
 * @param {string} text  PDB or mmCIF
 * @param {string[]} chains  auth or label asym ids
 * @returns {Promise<string>} mmCIF
 */
export async function extractChains(text, chains, { name = 'extracted' } = {}) {
    const wanted = new Set(chains ?? []);
    if (wanted.size === 0) throw new Error('no chain was named');

    const structure = await parseStructure(text);
    if (!structure) throw new Error('the structure could not be read');

    const query = Queries.generators.atoms({
        chainTest: ctx => wanted.has(StructureProperties.chain.auth_asym_id(ctx.element))
            || wanted.has(StructureProperties.chain.label_asym_id(ctx.element)),
        entityTest: ctx => StructureProperties.entity.type(ctx.element) === 'polymer',
    });
    const subset = StructureSelection.unionStructure(StructureQuery.run(query, structure));
    if (subset.elementCount === 0) {
        throw new Error(`no polymer residues in chain ${[...wanted].join(', ')}`);
    }
    // Copying the source categories would carry entries referring to the chains just removed.
    return to_mmCIF(name, subset, false, { copyAllCategories: false });
}

// Chain names a motif token can address.

const CHAIN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Can a motif token name a residue of this chain unambiguously?
 */
export function isNameableChain(chain) {
    return /^[A-Za-z]+$/.test(chain);
}

/** Alphabetic names, shortest first: A…Z, a…z, then AA, AB, … — 2756 of them before this runs dry. */
function* alphabeticNames() {
    for (const c of CHAIN_ALPHABET) yield c;
    for (const a of CHAIN_ALPHABET) for (const b of CHAIN_ALPHABET) yield a + b;
}

// Which items hold a chain id, per naming scheme.
const CHAIN_ITEMS = {
    auth: /(auth_asym_id|pdbx?_strand_id)$/,
    label: /(label_asym_id|asym_id_list|^_struct_asym\.id$|\.asym_id$)/,
};

/** Items whose value is a comma-separated list of chain ids rather than one. */
const CHAIN_LIST_ITEM = /(asym_id_list|strand_id)$/;

/** `_atom_site.auth_asym_id` is what listResidues and listChains read; without it they read label. */
function effectiveScheme(frame) {
    return frame.categories.atom_site?.fieldNames.includes('auth_asym_id') ? 'auth' : 'label';
}

/**
 * Give an alphabetic name to each chain a motif cannot address, and leave every other chain alone.
 * @param {string[]} chains
 * @returns {Map<string, string>} original -> alias, for the chains that were renamed
 */
export function planChainRenames(chains) {
    // Every chain that keeps its name is off limits as an alias, whatever its length.
    const taken = new Set(chains.filter(isNameableChain));
    const renames = new Map();
    const pending = [];

    // First pass: keep the initial letter where it is free, so A1 stays recognisably A.
    for (const chain of chains) {
        if (isNameableChain(chain)) continue;
        const first = chain[0];
        if (CHAIN_ALPHABET.includes(first) && !taken.has(first)) {
            taken.add(first);
            renames.set(chain, first);
        } else {
            pending.push(chain);
        }
    }

    // Second pass: the rest in name order, two-letter names once the single letters are used up.
    const names = alphabeticNames();
    for (const chain of pending) {
        let next = names.next();
        while (!next.done && taken.has(next.value)) next = names.next();
        if (next.done) break;                        // 2756 chains renamed: give up rather than guess
        taken.add(next.value);
        renames.set(chain, next.value);
    }
    return renames;
}

function renameChainsPdb(text, renames) {
    // A PDB chain field is a single column, so a long name cannot have come from one. Kept for
    // completeness: renaming single-character chains is still a legitimate request.
    return text.split('\n').map((line) => {
        const record = line.slice(0, 6);
        if (record !== 'ATOM  ' && record !== 'HETATM' && !line.startsWith('TER')) return line;
        if (line.length <= 21) return line;
        const alias = renames.get(line[21]);
        return alias ? `${line.slice(0, 21)}${alias}${line.slice(22)}` : line;
    }).join('\n');
}

/** Rewrite the chain-bearing columns of a parsed frame; null when nothing matched. */
function renameCifFrame(frame, renames, matches) {
    const categories = { ...frame.categories };
    let changed = false;

    for (const catName of frame.categoryNames) {
        const category = frame.categories[catName];
        const targets = category.fieldNames.filter(n => matches.test(`_${catName}.${n}`));
        if (targets.length === 0) continue;

        const fields = {};
        for (const fieldName of category.fieldNames) {
            const field = category.getField(fieldName);
            if (!targets.includes(fieldName)) {
                fields[fieldName] = field;
                continue;
            }
            const isList = CHAIN_LIST_ITEM.test(`_${catName}.${fieldName}`);
            const values = [];
            for (let i = 0; i < category.rowCount; i++) {
                const value = field.str(i);
                const next = isList
                    ? value.split(',').map(p => renames.get(p.trim()) ?? p).join(',')
                    : renames.get(value) ?? value;
                if (next !== value) changed = true;
                values.push(next);
            }
            fields[fieldName] = CifField.ofStrings(values);
        }
        categories[catName] = CifCategory.ofFields(catName, fields);
    }

    if (!changed) return null;
    return { header: frame.header, categoryNames: frame.categoryNames, categories };
}

/** Write a parsed frame back out, every category and row as it was read. */
function writeCifFrame(frame) {
    const encoder = CifWriter.createEncoder({ binary: false });
    encoder.startDataBlock(frame.header);

    for (const catName of frame.categoryNames) {
        const category = frame.categories[catName];
        const builder = CifWriter.fields();
        for (const fieldName of category.fieldNames) {
            const field = category.getField(fieldName);
            builder.str(fieldName, row => field.str(row));
        }
        const fields = builder.getFields();
        encoder.writeCategory({
            name: catName,
            instance: () => ({ fields, source: [{ data: undefined, rowCount: category.rowCount }] }),
        });
    }
    return encoder.getData();
}

/**
 * Rewrite a structure's chain names. An mmCIF is re-emitted from its parsed categories, so rows and
 * values survive but the original byte layout does not.
 *
 * @param {string} text
 * @param {Map<string, string>|object} renames  original -> alias
 * @param {{scheme?: 'auth'|'label'|'effective'}} [opts]  which mmCIF naming scheme to rewrite.
 *   Default 'effective': the one a reader would surface, which is `auth` when the file has auth
 *   columns and `label` when it does not
 * @returns {Promise<string>} the same structure with those chains renamed; unchanged if nothing matched
 */
export async function renameChains(text, renames, { scheme = 'effective' } = {}) {
    const map = renames instanceof Map ? renames : new Map(Object.entries(renames ?? {}));
    if (typeof text !== 'string' || map.size === 0) return text;
    if (!isCif(text)) return renameChainsPdb(text, map);

    const frame = await cifFrame(text);
    if (!frame) return text;

    const matches = CHAIN_ITEMS[scheme === 'effective' ? effectiveScheme(frame) : scheme];
    const renamed = renameCifFrame(frame, map, matches);
    return renamed ? writeCifFrame(renamed) : text;
}

// Text-level helpers molstar is not needed for.

const PDB_CHAIN_ID = [21, 22];

/**
 * PDB text as fixed-column records: whitespace-separated ATOM/HETATM lines are rewritten, blank
 * lines dropped, and CONECT records too when asked. mmCIF is returned as it is.
 *
 * @param {string} text
 * @param {{dropConect?: boolean}} [options]
 * @returns {string}
 */
export function normalizePdb(text, options = {}) {
    if (typeof text !== 'string' || isCif(text)) return text;
    return normalizeWhitespacePdb(text.trimStart(), options);
}

function normalizeWhitespacePdb(text, options = {}) {
    return text
        .split(/\r?\n/)
        .map(line => {
            const trimmed = line.trim();
            if (!trimmed) return '';
            if (options.dropConect && trimmed.startsWith('CONECT')) return '';
            if (!trimmed.startsWith('ATOM') && !trimmed.startsWith('HETATM')) {
                return trimmed;
            }
            if (hasFixedAtomColumns(line)) return line.trimEnd();
            return formatWhitespaceAtom(trimmed.split(/\s+/))?.trimEnd();
        })
        .filter(Boolean)
        .join('\n');
}

function hasFixedAtomColumns(line) {
    return line.length >= 54
        && Number.isFinite(Number.parseInt(line.slice(22, 26), 10))
        && Number.isFinite(Number.parseFloat(line.slice(30, 38)))
        && Number.isFinite(Number.parseFloat(line.slice(38, 46)))
        && Number.isFinite(Number.parseFloat(line.slice(46, 54)));
}

function formatWhitespaceAtom(parts) {
    if (parts.length < 9) return null;
    const hasChain = !/^-?\d+$/.test(parts[4]);
    const resSeqIndex = hasChain ? 5 : 4;
    const coordIndex = hasChain ? 6 : 5;
    const serial = Number.parseInt(parts[1], 10) || 1;
    const atomName = parts[2] || 'CA';
    const resName = parts[3] || 'UNK';
    const chain = hasChain ? parts[4] : 'A';
    const resSeq = Number.parseInt(parts[resSeqIndex], 10);
    const x = Number.parseFloat(parts[coordIndex]);
    const y = Number.parseFloat(parts[coordIndex + 1]);
    const z = Number.parseFloat(parts[coordIndex + 2]);
    const occupancy = Number.parseFloat(parts[coordIndex + 3]);
    const bFactor = Number.parseFloat(parts[coordIndex + 4]);
    if (!Number.isFinite(resSeq) || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;

    const element = (parts[coordIndex + 5] || atomName.replace(/[^A-Za-z]/g, '').slice(0, 2)).toUpperCase();
    return atomToPdbRow({
        group: parts[0] === 'HETATM' ? 'HETATM' : 'ATOM',
        serial,
        atomName,
        resName,
        chain,
        resno: resSeq,
        x,
        y,
        z,
        occupancy,
        bFactor,
        element,
        charge: parts[coordIndex + 6] || '',
    });
}

// pulchra drops the chain column, so callers that reconstruct a backbone have to put it back.
export function setChainId(text, chain) {
    if (typeof text !== 'string' || !chain) return text;
    return text.split('\n').map((line) => (
        line.startsWith('ATOM') || line.startsWith('HETATM')
            ? line.slice(0, PDB_CHAIN_ID[0]) + chain + line.slice(PDB_CHAIN_ID[1])
            : line
    )).join('\n');
}

/**
 * mmCIF values may be quoted ('A 1' or "A 1"). Splitting on whitespace alone would shift every
 * later column on such a row, so quoted runs are kept whole.
 */
function splitCifRowSpans(line) {
    const out = [];
    let i = 0;
    while (i < line.length) {
        while (i < line.length && /\s/.test(line[i])) i++;
        if (i >= line.length) break;
        const quote = line[i] === "'" || line[i] === '"' ? line[i] : null;
        if (quote) {
            const end = line.indexOf(quote, i + 1);
            if (end === -1) { out.push({ value: line.slice(i + 1), start: i, end: line.length }); break; }
            out.push({ value: line.slice(i + 1, end), start: i, end: end + 1 });
            i = end + 1;
        } else {
            let j = i;
            while (j < line.length && !/\s/.test(line[j])) j++;
            out.push({ value: line.slice(i, j), start: i, end: j });
            i = j;
        }
    }
    return out;
}

function splitCifRow(line) {
    return splitCifRowSpans(line).map(s => s.value);
}

// Joining several structures into one.

/** The `_atom_site` loop of an mmCIF string: its item names and its rows, split into values. */
function atomSiteLoop(text) {
    const lines = text.split('\n');
    let i = 0;
    while (i < lines.length) {
        if (lines[i].trim() !== 'loop_') { i++; continue; }
        i++;

        const headers = [];
        while (i < lines.length && lines[i].trim().startsWith('_')) {
            headers.push(lines[i].trim().split(/\s+/)[0]);
            i++;
        }
        if (headers.length === 0 || !headers[0].startsWith('_atom_site.')) continue;

        const rows = [];
        for (; i < lines.length; i++) {
            const t = lines[i].trim();
            if (t === '' || t === 'loop_' || t.startsWith('_') || t.startsWith('#')
                || t.startsWith('data_')) {
                break;
            }
            const cols = splitCifRow(t);
            if (cols.length === headers.length) rows.push(cols);
        }
        return { headers, rows };
    }
    return null;
}

/** Loop values are whitespace separated, so one holding a space has to be quoted back. */
function quoteCifValue(value) {
    if (value === undefined || value === '') return '.';
    return /[\s'"]/.test(value) ? `'${value.replace(/'/g, '')}'` : value;
}

/**
 * Join several mmCIF structures into one, the way concatenating ATOM records joins PDB ones.
 *
 * Only the `_atom_site` loop survives. A reader handed several `data_` blocks parses one and drops
 * the rest, so the extra structures have to arrive as extra rows of a single loop rather than as
 * their own blocks.
 *
 * @param {string[]} chunks
 * @returns {string} one mmCIF data block, or '' if no chunk had an atom_site loop
 */
export function mergeCifAtomSites(chunks) {
    const loops = chunks
        .map(chunk => (typeof chunk === 'string' ? atomSiteLoop(chunk) : null))
        .filter(Boolean);
    if (loops.length === 0) return '';

    const headers = loops[0].headers;
    const idColumn = headers.indexOf('_atom_site.id');
    const modelColumn = headers.indexOf('_atom_site.pdbx_PDB_model_num');
    const rows = [];
    let serial = 1;

    for (const loop of loops) {
        // A later file may order or omit items differently, so address its values by name.
        const columns = headers.map(header => loop.headers.indexOf(header));
        for (const row of loop.rows) {
            const values = columns.map(column => (column >= 0 ? row[column] : '.'));
            if (idColumn >= 0) values[idColumn] = String(serial);
            // Each file numbers its own model 1; keeping that would stack them as an ensemble.
            if (modelColumn >= 0) values[modelColumn] = '1';
            rows.push(values.map(quoteCifValue).join(' '));
            serial++;
        }
    }

    return ['data_merged', '#', 'loop_', ...headers, ...rows, '#', ''].join('\n');
}

/**
 * Drop `_chem_comp` rows that declare no component type.
 *
 * @param {string} text  mmCIF
 * @returns {string} the same text with untyped components removed; unchanged if there were none
 */
export function stripUntypedChemComp(text) {
    if (typeof text !== 'string' || !text.includes('_chem_comp.')) return text;

    const lines = text.split('\n');
    const out = [];
    let i = 0;
    let changed = false;

    while (i < lines.length) {
        if (lines[i].trim() !== 'loop_') { out.push(lines[i]); i++; continue; }

        const start = i;
        i++;
        const headers = [];
        while (i < lines.length && lines[i].trim().startsWith('_')) {
            headers.push(lines[i].trim().split(/\s+/)[0]);
            i++;
        }
        if (headers.length === 0 || !headers[0].startsWith('_chem_comp.')) {
            out.push(...lines.slice(start, i));
            continue;
        }

        const typeColumn = headers.indexOf('_chem_comp.type');
        const rows = [];
        for (; i < lines.length; i++) {
            const t = lines[i].trim();
            if (t === '' || t === 'loop_' || t.startsWith('_') || t.startsWith('#')
                || t.startsWith('data_')) {
                break;
            }
            const cols = splitCifRow(t);
            // '.' and '?' are mmCIF's null markers: the file is saying it does not know the type.
            if (typeColumn >= 0 && (cols[typeColumn] === '.' || cols[typeColumn] === '?')) {
                changed = true;
                continue;
            }
            rows.push(lines[i]);
        }
        if (rows.length > 0) out.push(lines[start], ...headers, ...rows);
    }

    return changed ? out.join('\n') : text;
}
